import {
  FieldClass,
  FieldType,
  IDataModelAddDerivedFieldInput,
  IDataModelAddFieldInput,
  IDataModelExtendFactFieldInput,
  IDataModelUpdateFieldInput,
  IDerivedFieldDefinitionInput,
  IDerivedFieldDefinitionMeta,
  IRawSqlDefinitionInput,
  ISchemaDataModelFieldsFragment,
  ISchemaFieldMetaFieldsFragment,
  Order,
} from '../../../../utils/apollo/graphqlSchema';
import {
  dataModelerSearchMatches,
  generateSortableColumnClickFunc,
  getAllModelFields,
  listToHashMapFromKey,
} from '../utils';
import { isNil } from 'lodash';
import { assertNotNil } from '../../../../utils/types';
import { useCallback, useMemo, useState } from 'react';
import { useAppSelector } from '../../../../redux/hooks';
import {
  dataModelerSelectCurrentDraftEdits,
  dataModelerSelectCurrentSchemaObj,
} from '../../../../redux/selectors/dataModeler';
import { defaultEmptyDraftEdits } from '../../../../redux/slices/dataModeler/dataModelerSlice';
import {
  IAddedDimensionDraftData,
  IDerivedFieldDefinitionDraft,
} from '../../../../redux/slices/dataModeler';
import {
  DATA_MODEL_FIELD_TYPE_CONFIG,
  generateUniqueDataModelName,
} from '../../../../redux/slices/dataModeler/utils';
import { UnreachableCaseError } from 'ts-essentials';

export const DERIVED_FIELD_EDIT_FORM_ANCHOR_ID = 'edit-derived-field-form';

export type SortableFieldColumnKeys =
  | 'default'
  | 'fieldClass'
  // | 'key'
  | 'tables'
  | 'name'
  | 'column'
  | 'description'
  | 'type'
  | 'filter'
  | 'groupby'
  | 'deprecated';

export interface ISortableEditorFieldItem {
  originalIndex: number;
  addFieldInput?: IDataModelAddFieldInput;
  extendFieldInputs: IDataModelExtendFactFieldInput[];
  editFieldInput?: IDataModelUpdateFieldInput;
  fieldMeta?: ISchemaFieldMetaFieldsFragment;
  derivedFieldInput?: IDataModelAddDerivedFieldInput;
  isRequired: boolean;
}

const fieldItemMatches = (
  searchQuery: string,
  item: ISortableEditorFieldItem,
) => {
  const { addFieldInput, derivedFieldInput, editFieldInput, fieldMeta } = item;
  return (
    dataModelerSearchMatches(searchQuery, addFieldInput) ||
    dataModelerSearchMatches(searchQuery, fieldMeta) ||
    dataModelerSearchMatches(searchQuery, editFieldInput) ||
    dataModelerSearchMatches(searchQuery, derivedFieldInput)
  );
};

const buildSortableFieldsList = (
  schemaObj: ISchemaDataModelFieldsFragment,
  addedFieldsInputs: IDataModelAddFieldInput[],
  updatedFieldsInputs: IDataModelUpdateFieldInput[],
  extendedFieldInputs: IDataModelExtendFactFieldInput[],
  addDerivedFieldInputs: IDataModelAddDerivedFieldInput[],
  addedDimensionsData: IAddedDimensionDraftData[],
): ISortableEditorFieldItem[] => {
  // Newly Added Fields referenced by temporal joins can not be removed (can still be hidden).
  // Only way to remove these drafts is to remove the join table.
  const requiredByTemporalJoinColumns = addedDimensionsData.flatMap((dim) => {
    if (!dim.joinData.temporalCondition) {
      return [];
    }
    const ret = [
      {
        viewKey: dim.viewKey,
        column: dim.joinData.temporalCondition.dimTemporalColumn,
      },
    ];
    if (dim.joinData.temporalCondition.dimSecondTemporalColumn) {
      ret.push({
        viewKey: dim.viewKey,
        column: dim.joinData.temporalCondition.dimSecondTemporalColumn,
      });
    }
    return ret;
  });

  const existingFields = getAllModelFields(schemaObj);
  const editsByKey = listToHashMapFromKey(updatedFieldsInputs, 'key');
  return [
    ...addDerivedFieldInputs.map((derivedFieldInput, originalIndex) => {
      return {
        originalIndex,
        extendFieldInputs: [],
        derivedFieldInput,
        isRequired: false,
      };
    }),
    ...addedFieldsInputs.map((addFieldInput, originalIndex) => {
      const extendFieldInputs = extendedFieldInputs.filter(
        (extendInput) => extendInput.fieldKey === addFieldInput.key,
      );
      return {
        originalIndex,
        addFieldInput,
        editFieldInput: undefined,
        extendFieldInputs,
        isRequired:
          addFieldInput.typeInput.fieldClass !== FieldClass.Basic ||
          requiredByTemporalJoinColumns.some(
            (reqJoinCol) =>
              addFieldInput.viewKey === reqJoinCol.viewKey &&
              addFieldInput.column === reqJoinCol.column,
          ),
      };
    }),
    ...existingFields.map((fieldMeta, originalIndex) => {
      const extendFieldInputs = extendedFieldInputs.filter(
        (extendInput) => extendInput.fieldKey === fieldMeta.key,
      );
      return {
        originalIndex,
        fieldMeta,
        editFieldInput: editsByKey[fieldMeta.key],
        extendFieldInputs,
        isRequired: true,
      };
    }),
  ];
};

const getStringPropFromSortableItem = (
  obj: ISortableEditorFieldItem,
  sortColumn: 'key' | 'name' | 'column' | 'description',
) => {
  const addColumnValue =
    obj.addFieldInput?.[sortColumn] ?? obj.derivedFieldInput?.[sortColumn];
  if (addColumnValue) {
    return addColumnValue;
  }
  const editColumnValue = obj.editFieldInput
    ? sortColumn in obj.editFieldInput && sortColumn !== 'column'
      ? obj.editFieldInput[sortColumn]
      : undefined
    : undefined;
  if (editColumnValue) {
    return editColumnValue;
  }
  return obj.fieldMeta?.[sortColumn] ?? '';
};

const getBoolPropFromSortableItem = (
  obj: ISortableEditorFieldItem,
  sortColumn: 'filter' | 'groupby' | 'deprecated',
) => {
  const addColumnValue =
    sortColumn !== 'deprecated'
      ? obj.addFieldInput?.[sortColumn] ?? obj.derivedFieldInput?.[sortColumn]
      : undefined;
  if (!isNil(addColumnValue)) {
    return addColumnValue;
  }
  const editColumnValue = obj.editFieldInput
    ? sortColumn in obj.editFieldInput
      ? obj.editFieldInput[sortColumn]
      : undefined
    : undefined;
  if (!isNil(editColumnValue)) {
    return editColumnValue;
  }
  return obj.fieldMeta?.[sortColumn] ?? false;
};

const getTableColumnsPropFromSortableItem = (obj: ISortableEditorFieldItem) => {
  const newColumn = obj.addFieldInput?.column ? [obj.addFieldInput.column] : [];
  const exitingColumns =
    obj.fieldMeta?.tableFields?.map((tf) => tf.column) ?? [];
  const extendColumns = obj.extendFieldInputs.map(
    (extendInput) => extendInput.column,
  );
  return [...newColumn, ...exitingColumns, ...extendColumns];
};

const FIELD_CLASS_SORT_ORDER: Record<FieldClass, number> = {
  [FieldClass.Event]: 0,
  [FieldClass.Temporal]: 1,
  [FieldClass.Subject]: 2,
  [FieldClass.Basic]: 3,
};

const compareItemsFunc = (
  sortColumn: SortableFieldColumnKeys,
): ((a: ISortableEditorFieldItem, b: ISortableEditorFieldItem) => number) => {
  return (a, b) => {
    switch (sortColumn) {
      case 'default':
        return !!a.addFieldInput < !!b.addFieldInput
          ? 1
          : !!a.derivedFieldInput < !!b.derivedFieldInput
            ? 1
            : !!a.editFieldInput < !!b.editFieldInput
              ? 1
              : -1;
      case 'fieldClass':
        const aClass =
          a.addFieldInput?.typeInput?.fieldClass ??
          a.fieldMeta?.fieldClass ??
          FieldClass.Basic;
        const bClass =
          b.addFieldInput?.typeInput?.fieldClass ??
          b.fieldMeta?.fieldClass ??
          FieldClass.Basic;
        return FIELD_CLASS_SORT_ORDER[aClass] - FIELD_CLASS_SORT_ORDER[bClass];
      case 'type':
        const aType =
          a.addFieldInput?.typeInput?.type ??
          a.derivedFieldInput?.typeInput?.type ??
          a.fieldMeta?.type ??
          FieldType.String;
        const bType =
          b.addFieldInput?.typeInput?.type ??
          b.derivedFieldInput?.typeInput?.type ??
          b.fieldMeta?.type ??
          FieldType.String;
        return aType.localeCompare(bType);
      case 'deprecated':
        // XXX this one is special, if same deprecated status - sort by class
        // (which also sorts by the ability to be deprecated)
        const adBool = getBoolPropFromSortableItem(a, sortColumn);
        const bdBool = getBoolPropFromSortableItem(b, sortColumn);
        if (adBool === bdBool) {
          const adClass =
            a.addFieldInput?.typeInput?.fieldClass ??
            a.fieldMeta?.fieldClass ??
            FieldClass.Basic;
          const bdClass =
            b.addFieldInput?.typeInput?.fieldClass ??
            b.fieldMeta?.fieldClass ??
            FieldClass.Basic;
          return (
            FIELD_CLASS_SORT_ORDER[adClass] - FIELD_CLASS_SORT_ORDER[bdClass]
          );
        }
        return adBool < bdBool ? -1 : 1;
      case 'filter':
      case 'groupby':
        const aBool = getBoolPropFromSortableItem(a, sortColumn);
        const bBool = getBoolPropFromSortableItem(b, sortColumn);
        return aBool < bBool ? -1 : 1;
      // case 'key':
      case 'name':
      case 'column':
      case 'description':
        const aKey = getStringPropFromSortableItem(a, sortColumn);
        const bKey = getStringPropFromSortableItem(b, sortColumn);
        return aKey.localeCompare(bKey);
      case 'tables':
        const aColumns = getTableColumnsPropFromSortableItem(a);
        const bColumns = getTableColumnsPropFromSortableItem(b);
        if (aColumns.length !== bColumns.length) {
          return bColumns.length - aColumns.length;
        }
        return aColumns.join(', ').localeCompare(bColumns.join(', '));
      default:
        throw new UnreachableCaseError(sortColumn);
    }
  };
};

const compareItemsWithOrderFunc = (
  sortColumn: SortableFieldColumnKeys,
  sortOrder: Order,
): ((a: ISortableEditorFieldItem, b: ISortableEditorFieldItem) => number) => {
  const compareFunc = compareItemsFunc(sortColumn);
  return (a, b) => {
    const compareRet = compareFunc(a, b);
    return sortOrder === Order.Asc ? compareRet : -compareRet;
  };
};

export const useDataModelFieldsGridModel = () => {
  const [sortOrderOrNull, setSortOrderOrNull] = useState<Order | null>(null);
  const [sortColumn, setSortColumn] =
    useState<SortableFieldColumnKeys>('default');

  const schemaObj = useAppSelector(dataModelerSelectCurrentSchemaObj);
  assertNotNil(schemaObj);
  const draftEdits =
    useAppSelector(dataModelerSelectCurrentDraftEdits) ??
    defaultEmptyDraftEdits;
  const {
    addedDimensionsData,
    draftFieldsData,
    updatedFieldsInputs,
    draftDerivedFields,
  } = draftEdits;

  const addedFieldsInputs = useMemo(() => {
    return draftFieldsData.flatMap(({ addInput }) =>
      addInput ? [addInput] : [],
    );
  }, [draftFieldsData]);

  const extendedFieldsInputs = useMemo(() => {
    return draftFieldsData.flatMap(({ extendInput }) =>
      extendInput ? [extendInput] : [],
    );
  }, [draftFieldsData]);

  const originalList = useMemo(() => {
    return buildSortableFieldsList(
      schemaObj,
      addedFieldsInputs,
      updatedFieldsInputs,
      extendedFieldsInputs,
      draftDerivedFields,
      addedDimensionsData,
    );
  }, [
    addedDimensionsData,
    addedFieldsInputs,
    extendedFieldsInputs,
    schemaObj,
    updatedFieldsInputs,
    draftDerivedFields,
  ]);

  const sortedList = useMemo(() => {
    if (sortOrderOrNull === null) {
      return originalList;
    }
    return originalList.sort(
      compareItemsWithOrderFunc(sortColumn, sortOrderOrNull),
    );
  }, [sortOrderOrNull, originalList, sortColumn]);

  const onColumnClickFn = useCallback(
    (forSortColumn: SortableFieldColumnKeys) => {
      return generateSortableColumnClickFunc(
        forSortColumn,
        sortColumn,
        setSortColumn,
        setSortOrderOrNull,
      );
    },
    [sortColumn],
  );

  return {
    sortedList,
    sortColumn,
    sortOrderOrNull,
    onColumnClickFn,
    fieldItemMatches,
    schemaObj,
  };
};

const rawSqlDerivedFieldInputToMeta = (
  input: IRawSqlDefinitionInput,
): IDerivedFieldDefinitionMeta => {
  return {
    __typename: 'RawSqlDerivedFieldMeta',
    rawSql: input.rawSql,
    fullDataRequired: input.fullDataRequired,
  };
};

export const derivedFieldDefinitionInputToMeta = (
  input: IDerivedFieldDefinitionInput,
): IDerivedFieldDefinitionMeta => rawSqlDerivedFieldInputToMeta(input.rawSql);

export const addDerivedFieldInputToMeta = (
  derivedField: IDataModelAddDerivedFieldInput,
): IDerivedFieldDefinitionMeta =>
  rawSqlDerivedFieldInputToMeta(derivedField.rawSql);

export const defaultNewDerivedFieldDefinitionDraft = (
  allOtherColumns: Set<string>,
): IDerivedFieldDefinitionDraft => {
  const draftColumnName = generateUniqueDataModelName('derived_field', null, [
    ...allOtherColumns.keys(),
  ]);
  return {
    fieldName: draftColumnName,
    fieldType: FieldType.String,
    typeParams: {
      __typename: 'FieldTypeParams',
      fixedTz: null,
      linearDistribution: false,
      arrayItemType: null,
      customDefinition: null,
      structDefinition: null,
      mapDefinition: null,
    },
    rawSql: {
      rawSql: '',
      fullDataRequired: false,
    },
    fieldMeta: null,
    newFieldKey: null,
  };
};

const INVALID_COLUMN_CHARS_REPLACE_REGEX = /[^0-9a-zA-Z_]+/g;

const generateUniqueColumnFromName = (
  name: string,
  allOtherNames: string[],
) => {
  const desiredName = name
    .toLowerCase()
    .replaceAll(INVALID_COLUMN_CHARS_REPLACE_REGEX, '_');
  return generateUniqueDataModelName(desiredName, null, allOtherNames);
};

export const newDraftDerivedFieldToInput = (
  draft: IDerivedFieldDefinitionDraft,
  allOtherColumnsSet: Set<string>,
): IDataModelAddDerivedFieldInput => {
  assertNotNil(draft.typeParams);
  const columnName = generateUniqueColumnFromName(draft.fieldName, [
    ...allOtherColumnsSet.values(),
  ]);
  const groupby =
    draft.fieldType !== FieldType.Array &&
    DATA_MODEL_FIELD_TYPE_CONFIG[draft.fieldType].defaultGroupby;
  return {
    column: columnName,
    description: null,
    filter: true,
    groupby,
    key: columnName.toUpperCase(),
    name: draft.fieldName,
    rawSql: draft.rawSql,
    typeInput: {
      fieldClass: FieldClass.Basic,
      type: draft.fieldType,
      typeParams: {
        fixedTz: draft.typeParams.fixedTz,
        arrayItemType: draft.typeParams.arrayItemType,
        linearDistribution: draft.typeParams.linearDistribution,
        customDefinition: null,
        structDefinition: null,
        mapDefinition: null,
      },
    },
  };
};
